
import { Vue, Component, Prop } from "vue-property-decorator";
import getLocationInfo from "@/helpers/locations/getLocationInfo";
import CardBodyItem from "@/components/card/CardBodyItemNew.vue";
import ResourceCards from "@/components/card/ResourceCards.vue";
import ProjectChip from "@/components/managers/ProjectChip.vue";
import TableAvatar from "@/components/table/TableAvatar.vue";
import TableIcon from "@/components/table/table-icon.vue";
import ContractorChips from "@/components/companyProjects/ContractorChips.vue";
import ResourceAvatar from "@/components/card/ResourceAvatar.vue";
import ItemChipsWithMore from "@/components/common/ItemChipsWithMore.vue";
import ColorIconContainer from "@/components/icons/ColorIconContainer.vue";
import UiButton from "@/components/button/ui-button.vue";
import ActionSection from "@/components/card/ActionSection.vue";
import ChatIcon from "@/components/icons/ChatIcon.vue";
import ManagerInvitationDisabledTooltip from "@/components/managers/ManagerInvitationDisabledTooltip.vue";
import ManagerResendInvitation from "@/components/invite/ManagerResendInvitation.vue";
import ResendInvitationPlaceholder from "@/components/invite/ResendInvitationPlaceholder.vue";
import Badge from "@/components/common/BadgeNew.vue";
import CountryFlag from "vue-country-flag";
import { Manager, Project, User } from "@/generated/graphql";
import RouteNames from "@/entities/RouteNames";
import { NOTIFICATION } from "@/entities/Events";
import Message from "@/data/notificationsMessages";
import getFullName from "@/helpers/getFullName";
import DELETE_MANAGERS_ACCESSED_CONTRACTOR from "@/apollo/managers/DeleteManagersAccessedContractor.gql";
import { emailStatusText, managerSuspendedText } from "@/data/userStatuses";
import ManagerStatus from "@/entities/manager/ManagerStatus";
import RouteParams from "@/entities/RouteParams";
import BadgeColor from "@/entities/BadgeColor";
import CardBodyItemLink from "@/components/card/CardBodyItemLink.vue";

interface IManagerProject {
  id: string;
  name: string;
}

interface IActionDefault {
  leftTitle: string;
  leftValue: number | string;
  rightTitle: string;
  rightValue: number | string;
}

interface IActionError {
  title: string;
  description: number | string;
  actionButton: string;
}

interface IActionSection {
  state: boolean;
  default: IActionDefault;
  error: IActionError;
}

@Component({
  components: {
    CardBodyItemLink,
    Badge,
    ManagerResendInvitation,
    CardBodyItem,
    ProjectChip,
    ResourceCards,
    TableAvatar,
    TableIcon,
    ContractorChips,
    ResourceAvatar,
    ItemChipsWithMore,
    ColorIconContainer,
    UiButton,
    ChatIcon,
    ManagerInvitationDisabledTooltip,
    ResendInvitationPlaceholder,
    ActionSection,
    CountryFlag,
  },
})
export default class ManagerCardResources extends Vue {
  @Prop({ type: String, required: true }) readonly firstName!: string;
  @Prop({ type: String, required: true }) readonly lastName!: string;
  @Prop({ type: String || null }) readonly country!: string | null;
  @Prop({ type: String, default: undefined }) readonly jobTitle?: string;
  @Prop({ type: String, default: undefined }) readonly location?: string;
  @Prop({ default: undefined }) readonly skills?: any;
  @Prop({ type: Boolean, default: undefined }) readonly confirmed?: boolean;
  @Prop({ type: String, default: undefined }) readonly managerTotal?: string;
  @Prop({ type: String, required: true }) readonly relationId!: string;
  @Prop({ type: String, default: undefined }) readonly avatar?: string;
  @Prop({ type: String, default: undefined }) readonly status?: string;
  @Prop({ type: Object, required: true }) readonly manager!: Manager;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: String, required: true }) readonly managerId!: string;
  @Prop({ type: Object, required: true }) readonly profile!: any;

  BadgeColor: typeof BadgeColor = BadgeColor;
  routes = RouteNames;
  emailStatusText: string = emailStatusText;
  managerSuspendedText: string = "Account disabled";
  RouteNames: typeof RouteNames = RouteNames;

  get isEmailUnconfirmed(): boolean {
    return !this.confirmed;
  }

  get managerLocation(): string {
    return getLocationInfo(this.profile!);
  }

  get managerRouteParams(): RouteParams<{ id: string }> {
    return {
      name: RouteNames.MANAGER_ABOUT,
      params: { id: this.manager.id },
    };
  }

 get managerHref(): string | undefined {
      if (!this.manager) {
        return undefined;
      }
      return `/managers/${this.manager.id}/about`;
    }

  get locationString(): string {
    if (!this.location) {
      return "n/a";
    }
    return this.location;
  }

  // onActivateManager(): undefined {
  // 		if (this.allowedActivateManagers) {
  // 			this.$emit('activate', this.manager.id);
  // 			return;
  // 		}

  // 		this.$router.push({
  // 			name: RouteNames.PRICING,
  // 		});
  // 	}

  // get allowedActivateManagers(): boolean {
  // 	if (this.isManagersCountUnlimited) {
  // 		return true;
  // 	}

  // 	return this.accessAllowedManagersCount > this.activeManagersCount;
  // }

  // get isManagersCountUnlimited(): boolean {
  // 	return !this.currentUser.subscription_required;
  // }

  removeAccess(): void {
    this.$confirm({
      title: `Remove access to ${getFullName(this.firstName, this.lastName)}`,
      message: `Are you sure?`,
      button: {
        no: "No",
        yes: "Yes",
      },
      callback: async (confirm: any) => {
        if (confirm) {
          this.removeManagerAccessToContractor();
        }
      },
    });
  }

  async removeManagerAccessToContractor(): Promise<void> {
    try {
      await this.removeAccessToContractor();
      await this.$emit("managerAccessRemoved");
      this.$eventBus.emit(
        NOTIFICATION,
        Message.successMessage(`Access was removed`)
      );
    } catch (e) {
      this.$eventBus.emit(NOTIFICATION, Message.errorMessage(e.message));
    }
  }

  removeAccessToContractor() {
    return this.$apollo.mutate({
      mutation: DELETE_MANAGERS_ACCESSED_CONTRACTOR,
      variables: {
        id: this.relationId,
      },
    });
  }

  get managerAvatar(): string | undefined {
    return this.avatar || undefined;
  }

  get managerProjects() {
    const projects: Project[] = this.manager.projects || [];

    return projects.map((p) => {
      return {
        id: p.id,
        name: p.name ? p.name : "",
      };
    });
  }

  isThereAnyErrorMessage(): boolean {
    if (this.isManagerSuspended || this.isEmailUnconfirmed) return true;
    return false;
  }

  get getActionSectionValues(): IActionSection {
    // use this function to get the values for the action section

    const actionSectionResult: IActionSection = {
      state: this.isThereAnyErrorMessage(),
      default: {
        leftTitle: "Active Client",
        leftValue: this.managerProjects.length,
        rightTitle: "Resources",
        rightValue: this.manager.contractors.length,
      },
      error: {
        title:
          this.isEmailUnconfirmed && !this.isManagerSuspended
            ? this.emailStatusText
            : this.managerSuspendedText,
        description: "",
        actionButton:
          this.isEmailUnconfirmed && !this.isManagerSuspended
            ? "resend"
            : "activate",
      },
    };
    return actionSectionResult;
  }

  get managerInitials(): string {
    return `${this.firstName![0]}${this.lastName![0]}`;
  }

  get isManagerSuspended(): boolean {
    return this.status === ManagerStatus.SUSPENDED;
  }

  get managerFullName(): string {
    return `${this.firstName} ${this.lastName}`;
  }

  get managerJobTitle(): string | undefined {
    if (!this.jobTitle) {
      return undefined;
    }
    return this.jobTitle
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }

  get managerLink(): RouteParams<{ id: string }> {
    return {
      name: RouteNames.MANAGER_ABOUT,
      params: { id: this.manager.id },
    };
  }

  get contractorsLink(): RouteParams<{ id: string }> {
    return {
      name: RouteNames.MANAGER_CONTRACTORS,
      params: { id: this.manager.id },
    };
  }

  get projectsLink(): RouteParams<{ id: string }> {
    return {
      name: RouteNames.MANAGER_PROJECTS,
      params: { id: this.manager.id },
    };
  }
}
